import * as i0 from '@angular/core';
import { Injectable, NgModule, Component, Input } from '@angular/core';
import { FormControl, Validators, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ChangeSourceType } from '@formbird/types';
import { debounceTime } from 'rxjs/operators';
import * as i1 from '@formbird/services';
import { ClientConstants } from '@formbird/services';

class ComponentSetupService {
    constructor() {
        this.formGroups = {};
    }
    setupStandardProperties(component) {
        const compDef = component.componentDefinition;
        if (compDef) {
            component.disabled = compDef.enabled === false;
        }
        component.elementName = component.fieldName;
        component.registeredControlName = component.fieldName + component.key;
    }
    /**
     * setup standard component variables. This can be called from the ngOnInit function of a component with:
     * this.componentService.setupStandardComponent(this);
     * @return the responsible control that holds the component value
     */
    setupStandardComponent(component, validators) {
        this.setupStandardProperties(component);
        const documentId = component.document.documentId;
        const formGroup = this.ensureFormGroupExisted(documentId);
        const compDef = component.componentDefinition;
        this.setupDefaultValidator(compDef, validators);
        const control = new FormControl({ value: component.fieldValue, disabled: this.isComponentDisabled(compDef) }, validators);
        return formGroup.registerControl(compDef.name + component.key, control);
    }
    setupControl(component, fieldName, fieldValue, validators) {
        const formGroup = this.ensureFormGroupExisted(component.document.documentId);
        const control = new FormControl(fieldValue, validators);
        return formGroup.registerControl(fieldName + component.key, control);
    }
    enableField(tplItem, formControl) {
        if (formControl) {
            if (tplItem.enabled === false) {
                formControl.disable({ emitEvent: false });
            }
            else {
                formControl.enable({ emitEvent: false });
            }
        }
    }
    enableFieldByFlag(enabled, formControl) {
        if (formControl) {
            if (enabled === false) {
                formControl.disable({ emitEvent: false });
            }
            else {
                formControl.enable({ emitEvent: false });
            }
        }
    }
    setupDefaultValidator(componentDefinition, validatorOpts) {
        validatorOpts = validatorOpts ? validatorOpts : [];
        if (componentDefinition.mandatory) {
            validatorOpts.push(Validators.required);
        }
    }
    isComponentDisabled(compDef) {
        return compDef.enabled === false;
    }
    ensureFormGroupExisted(documentId) {
        if (!this.formGroups[documentId]) {
            this.formGroups[documentId] = new FormGroup({});
        }
        return this.formGroups[documentId];
    }
    getControl(documentId, path) {
        if (this.formGroups[documentId]) {
            const formGroup = this.formGroups[documentId];
            return formGroup.get(path);
        }
        return null;
    }
}
/** @nocollapse */ /** @nocollapse */ ComponentSetupService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ComponentSetupService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ ComponentSetupService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ComponentSetupService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ComponentSetupService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class FormbirdAngularSharedModule {
}
/** @nocollapse */ /** @nocollapse */ FormbirdAngularSharedModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdAngularSharedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ /** @nocollapse */ FormbirdAngularSharedModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdAngularSharedModule });
/** @nocollapse */ /** @nocollapse */ FormbirdAngularSharedModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdAngularSharedModule, providers: [ComponentSetupService], imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: FormbirdAngularSharedModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                    providers: [ComponentSetupService]
                }]
        }] });

/** Error when invalid control is dirty, touched, or submitted. */
class MyErrorStateMatcher {
    isErrorState(control, form) {
        const isSubmitted = form && form.submitted;
        return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}
class AbstractComponent {
    constructor(formbirdInjector, changedDocumentService, componentSetupService, cdr) {
        this.formbirdInjector = formbirdInjector;
        this.changedDocumentService = changedDocumentService;
        this.componentSetupService = componentSetupService;
        this.cdr = cdr;
        /**
         * A sub-class could avoid calling notifyComponentIntialized on super-class by disabling this flag,
         * they will call that function at the end of their ngOnInit in stead
         * @default true to enable
         */
        this.shouldNotifyCompInitializedInSuper = true;
        this.shouldDispatchEmptyFieldValue = true;
        this.subs = new Subscription();
        this.matcher = new MyErrorStateMatcher();
        this.broadcastService = formbirdInjector.get('BroadcastService');
    }
    ngOnInit() {
        this.setupComponent();
        this.initializeFieldValue();
        this.setupChangeDetection();
        if (this.shouldNotifyCompInitializedInSuper) {
            this.notifyComponentIntialized();
        }
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    setupComponent() {
        this.formControl = this.componentSetupService.setupStandardComponent(this, this.validators);
        this.setFieldActivation(false);
    }
    setupChangeDetection(avoidDebounceTime) {
        if (this.fieldName) {
            const docSub = this.changedDocumentService.watch(this.fieldName, this.document.documentId, this.registeredControlName)
                .subscribe(fieldValue => {
                this.onFieldValueChanged(fieldValue);
            });
            this.subs.add(docSub);
        }
        const compSub = this.changedDocumentService.watchComponentDef(this.fieldName, this.template.documentId).subscribe(compDef => {
            this.onComponentDefChanged(compDef);
        });
        this.subs.add(compSub);
        if (this.formControl) {
            let changeSub;
            if (avoidDebounceTime) {
                changeSub = this.formControl.valueChanges.subscribe(value => {
                    this.onControlValueChanged(value);
                });
            }
            else {
                changeSub = this.formControl.valueChanges.pipe(debounceTime(ClientConstants.VALIDATION_INTERVAL_TIME)).subscribe(value => {
                    this.onControlValueChanged(value);
                });
            }
            this.subs.add(changeSub);
        }
        this.setupDocumentCreatedDetection();
    }
    setFieldActivation(shouldDetectChanges) {
        this.componentSetupService.enableField(this.componentDefinition, this.formControl);
        this.detectChanges(shouldDetectChanges);
    }
    /**
     * Call this function to dispatch the field value change action to store
     */
    valueChanged(value) {
        this.fieldValue = value;
        this.changedDocumentService.valueChanged(this, value, this.registeredControlName);
    }
    /**
     * Call this function to set the field value into store. Param isInitValue is for initializing value and does not trigger undo/redo
     */
    dispatchChanged(isInitValue) {
        const documentId = this.document.documentId;
        const documentChangedInfo = {
            documentId: documentId,
            fieldName: this.fieldName,
            newValue: this.fieldValue,
            isInitValue: isInitValue,
            sourceType: ChangeSourceType.COMPONENT
        };
        this.changedDocumentService.notifyDocumentChanged(documentChangedInfo);
    }
    detectChanges(shouldDetectChange) {
        if (shouldDetectChange !== false && !this.cdr.destroyed) {
            this.cdr.detectChanges();
        }
    }
    updateComponentDefChange(componentDefinition) {
        this.componentDefinition = componentDefinition;
    }
    /**
     * This function will be called once user interacts with the control that manages the field value in the component.
     */
    onControlValueChanged(value) {
        this.valueChanged(value);
    }
    /**
     * This function will be called once the template component definition in store gets changed.
     */
    onComponentDefChanged(componentDefinition) {
        this.updateComponentDefChange(componentDefinition);
        const disabled = componentDefinition.enabled === false;
        if (this.disabled !== disabled) {
            this.disabled = disabled;
            this.setFieldActivation();
        }
        else {
            this.detectChanges();
        }
    }
    /**
     * This function is used to compare the newValue to the component fieldValue to check if they are the same.
     * For most components, the field value is primary types (string, number, boolean...) but some complex ones the field value type is object so the component needs to override this function to check correctly.
     * @param newValue
     * @returns true if newValue equals to the component fieldValue
     */
    isEqualFieldValue(newValue) {
        return this.fieldValue === newValue;
    }
    /**
     * This function will be called once the field value in store gets changed.
     */
    onFieldValueChanged(fieldValue, shouldDetectChanges = true) {
        if (this.isEqualFieldValue(fieldValue)) {
            return;
        }
        this.fieldValue = fieldValue;
        if (this.formControl) {
            this.formControl.patchValue(this.fieldValue, { emitEvent: false, onlySelf: true });
        }
        if (shouldDetectChanges) {
            this.detectChanges();
        }
    }
    setupDocumentCreatedDetection() {
        const docCreatedSub = this.broadcastService.on(ClientConstants.DOCUMENT_NEW).subscribe((documentInfo) => {
            this.onDocumentCreated(documentInfo);
            this.detectChanges();
        });
        this.subs.add(docCreatedSub);
    }
    /**
     * This function will be called to initialize field value such as: default value... once the New Document button's clicked to create a new document.
     * @param documentInfo includes documentId and unsavedDocumentListId
     */
    onDocumentCreated(documentInfo) {
        const document = documentInfo === null || documentInfo === void 0 ? void 0 : documentInfo.document;
        if (document) {
            const unsavedDocumentListId = documentInfo === null || documentInfo === void 0 ? void 0 : documentInfo.unsavedDocumentListId;
            const documentId = document.documentId;
            if (unsavedDocumentListId === this.formParameters.unsavedDocumentListId && this.document.documentId === documentId) {
                // set fieldValue as the origninal field value object
                this.fieldValue = document[this.fieldName];
                // setup fieldValue same as the component initialize in ngOnInit
                this.initializeFieldValue();
                // notify store the component's initialized
                this.notifyComponentIntialized();
            }
        }
    }
    /**
     * This function will initialize the field value including default value if needed
     */
    initializeFieldValue() {
    }
    /**
     * notify store the component has been initialized
     */
    notifyComponentIntialized() {
        this.changedDocumentService.notifyIntializedField(this, this.shouldDispatchEmptyFieldValue);
    }
    /**
     * Notify the component definition changes to store
     */
    notifyComponentDefChanged() {
    }
}
/** @nocollapse */ /** @nocollapse */ AbstractComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: AbstractComponent, deps: [{ token: i1.FormbirdInjectorService }, { token: i1.ChangedDocumentService }, { token: ComponentSetupService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ /** @nocollapse */ AbstractComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.1", type: AbstractComponent, selector: "ng-component", inputs: { document: "document", fieldValue: "fieldValue", template: "template", fieldName: "fieldName", formParameters: "formParameters", key: "key", responsiveLayouts: "responsiveLayouts", message: "message", componentDefinition: "componentDefinition" }, ngImport: i0, template: '' // template will be overridden in child component
    ,
    isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: AbstractComponent, decorators: [{
            type: Component,
            args: [{
                    template: '' // template will be overridden in child component
                }]
        }], ctorParameters: function () { return [{ type: i1.FormbirdInjectorService }, { type: i1.ChangedDocumentService }, { type: ComponentSetupService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { document: [{
                type: Input
            }], fieldValue: [{
                type: Input
            }], template: [{
                type: Input
            }], fieldName: [{
                type: Input
            }], formParameters: [{
                type: Input
            }], key: [{
                type: Input
            }], responsiveLayouts: [{
                type: Input
            }], message: [{
                type: Input
            }], componentDefinition: [{
                type: Input
            }] } });

class AbstractDocumentComponent extends AbstractComponent {
    constructor(formbirdInjectorService, changedDocumentService, componentSetupService, cdr) {
        super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);
        this.formbirdInjectorService = formbirdInjectorService;
        this.changedDocumentService = changedDocumentService;
        this.componentSetupService = componentSetupService;
        this.cdr = cdr;
        this.subs = new Subscription();
    }
    ngOnInit() {
        super.ngOnInit();
        const docSub = this.changedDocumentService.watchDocument(this.document.documentId).subscribe(doc => {
            this.document = doc;
        });
        this.subs.add(docSub);
    }
}
/** @nocollapse */ /** @nocollapse */ AbstractDocumentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: AbstractDocumentComponent, deps: [{ token: i1.FormbirdInjectorService }, { token: i1.ChangedDocumentService }, { token: ComponentSetupService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ /** @nocollapse */ AbstractDocumentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.1", type: AbstractDocumentComponent, selector: "ng-component", usesInheritance: true, ngImport: i0, template: '' // template will be overridden in child component
    ,
    isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: AbstractDocumentComponent, decorators: [{
            type: Component,
            args: [{
                    template: '' // template will be overridden in child component
                }]
        }], ctorParameters: function () { return [{ type: i1.FormbirdInjectorService }, { type: i1.ChangedDocumentService }, { type: ComponentSetupService }, { type: i0.ChangeDetectorRef }]; } });

/*
 * Public API Surface of @formbird/angular-shared
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractComponent, AbstractDocumentComponent, ComponentSetupService, FormbirdAngularSharedModule, MyErrorStateMatcher };
